// React
import { useState } from "react";

// Formik
import { useFormik } from "formik";

// Libraries
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Material UI
import { Button, Stack } from "@mui/material";

// Validation
import { mbsForgotPasswordValidationSchema } from "Validation/UserValidation";

// Common
import { FormikTextField } from "Common/Components";

// Components
import MBSAuthFormHeader from "./MBSAuthFormHeader";
import MBSPasswordFields from "./MBSPasswordFields";
import { OTP } from "Components/Authentication/Components/OTP";

const MBSForgotPasswordForm = ({
  initialEmail = "",
  onRequestCode,
  onSubmit,
  onBackToSignIn,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasPrefilledEmail = Boolean(initialEmail.trim());
  const [isCodeStep, setIsCodeStep] = useState(hasPrefilledEmail);
  const [isRequestingCode, setIsRequestingCode] = useState(false);

  const formik = useFormik({
    initialValues: {
      userEmail: initialEmail,
      code: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    enableReinitialize: true,
    validationSchema: mbsForgotPasswordValidationSchema(t, { isCodeStep }),
    onSubmit: async (values) => {
      try {
        await onSubmit(
          values.userEmail.trim(),
          values.code,
          values.newPassword
        );
        formik.resetForm();
        dispatch(
          openSnackbar({
            message: t("mbsAuth.feedback.passwordReset"),
            severity: "success",
          })
        );
        setIsCodeStep(false);
        onBackToSignIn();
      } catch (error) {
        dispatch(
          openSnackbar({
            message: error.message || t("mbsAuth.errors.generic"),
            severity: "error",
          })
        );
      }
    },
  });

  const handleRequestCodeStep = async () => {
    formik.setTouched({ userEmail: true });

    const errors = await formik.validateForm();
    if (errors.userEmail) {
      return;
    }
    try {
      setIsRequestingCode(true);
      await onRequestCode(formik.values.userEmail.trim());
      setIsCodeStep(true);
    } catch (error) {
      dispatch(
        openSnackbar({
          message: error.message || t("mbsAuth.errors.generic"),
          severity: "error",
        })
      );
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleResendCode = async () => {
    try {
      await onRequestCode(formik.values.userEmail.trim());
    } catch (error) {
      dispatch(
        openSnackbar({
          message: error.message || t("mbsAuth.errors.generic"),
          severity: "error",
        })
      );
    }
  };

  const handleStepBack = () => {
    if (hasPrefilledEmail) {
      onBackToSignIn();
      return;
    }
    formik.resetForm({
      values: {
        ...formik.values,
        code: "",
        newPassword: "",
        confirmNewPassword: "",
      },
    });
    setIsCodeStep(false);
  };

  return (
    <Stack spacing={3}>
      <MBSAuthFormHeader
        title={t("mbsAuth.forgotPasswordTitle")}
        description={
          isCodeStep
            ? t("mbsAuth.forgotPasswordCodeStepMessage")
            : t("mbsAuth.forgotPasswordRequestMessage")
        }
      />

      {!isCodeStep ? (
        <Stack spacing={2}>
          <FormikTextField
            formik={formik}
            name="userEmail"
            field={{
              type: "email",
              label: t("mbsAuth.userEmail"),
              required: false,
              autoComplete: "email",
            }}
          />
          <Stack spacing={2} alignItems="center">
            <Button
              type="button"
              variant="contained"
              disabled={isRequestingCode}
              onClick={handleRequestCodeStep}
              fullWidth
            >
              {isRequestingCode
                ? t("mbsAuth.sendingCode")
                : t("mbsAuth.sendCode")}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={onBackToSignIn}
              disabled={isRequestingCode}
            >
              {t("mbsAuth.backToSignIn")}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack
          component="form"
          noValidate
          onSubmit={formik.handleSubmit}
          spacing={2}
        >
          <FormikTextField
            formik={formik}
            name="userEmail"
            field={{
              type: "email",
              label: t("mbsAuth.userEmail"),
              required: false,
              disabled: true,
            }}
          />
          <OTP
            setCode={formik.setFieldValue}
            resendCode={handleResendCode}
            values={formik.values}
            handleChange={formik.handleChange("code")}
            errors={formik.errors}
            touched={formik.touched}
          />
          <MBSPasswordFields formik={formik} />
          <Stack spacing={2} alignItems="center">
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              fullWidth
            >
              {formik.isSubmitting
                ? t("mbsAuth.resettingPassword")
                : t("mbsAuth.resetPassword")}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={handleStepBack}
              disabled={formik.isSubmitting}
            >
              {hasPrefilledEmail
                ? t("mbsAuth.backToSignIn")
                : t("mbsAuth.useDifferentEmail")}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default MBSForgotPasswordForm;
